import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Download, LogOut, ArrowLeft, Package, Code2, Calendar } from "lucide-react";
import { adminPing } from "@/lib/api";
import { useSEO } from "@/lib/useSEO";
import { Gate, KEY_STORAGE } from "@/pages/Admin";

const KIT = [
  {
    file: "primary-4k.png",
    label: "Primary Icon · 4K",
    caption: "4096×4096 puzzle icon. For posters, wall vinyl, large-format print.",
    bg: "obsidian",
  },
  {
    file: "horizontal-lockup.png",
    label: "Horizontal Lockup · Dark",
    caption: "Icon + wordmark on obsidian. Email headers, LinkedIn cover, wide banners.",
    bg: "obsidian",
  },
  {
    file: "horizontal-lockup-light.png",
    label: "Horizontal Lockup · Light",
    caption: "Icon + wordmark on cream. Letterheads, invoices, light collateral.",
    bg: "linen",
  },
  {
    file: "stacked-lockup.png",
    label: "Stacked Lockup",
    caption: "Icon over wordmark. Instagram grid, business card, tote.",
    bg: "obsidian",
  },
  {
    file: "with-tagline.png",
    label: "Billboard · With Tagline",
    caption: "Full brand billboard. Trade shows, showroom signage.",
    bg: "obsidian",
  },
  {
    file: "business-card-front.png",
    label: "Business Card · Front",
    caption: "90×55 mm at 300 DPI with 3 mm bleed. Obsidian stock, icon + wordmark + tagline.",
    bg: "obsidian",
  },
  {
    file: "business-card-back.png",
    label: "Business Card · Back",
    caption: "90×55 mm at 300 DPI with 3 mm bleed. Cream stock, editorial contact block — replace [Name], [Role] and contact placeholders.",
    bg: "linen",
  },
  {
    file: "story-title.png",
    label: "IG Story · Title",
    caption: "1080×1920 editorial title card for launches, new drops.",
    bg: "obsidian",
  },
  {
    file: "story-quote.png",
    label: "IG Story · Statement",
    caption: "1080×1920 big serif quote card for the studio voice.",
    bg: "obsidian",
  },
  {
    file: "story-material.png",
    label: "IG Story · Material Spotlight",
    caption: "1080×1920 template with upper image area — drop a product photo in and post.",
    bg: "obsidian",
  },
  {
    file: "letterhead-cream.png",
    label: "Letterhead · Cream",
    caption: "A4 300 DPI print letterhead. Icon + wordmark header, three-column footer.",
    bg: "linen",
  },
  {
    file: "letterhead-obsidian.png",
    label: "Letterhead · Obsidian",
    caption: "A4 300 DPI dark stock variant of the letterhead.",
    bg: "obsidian",
  },
  {
    file: "email-signature-banner.png",
    label: "Email Signature Banner",
    caption: "640×160 transparent banner — embed at the top of your email signature.",
    bg: "linen",
  },
  {
    file: "signature-icon.png",
    label: "Signature Icon · 120×120",
    caption: "Tight-cropped puzzle mark, sized 1:1 for the icon slot in the email signature template. Drop-in for the HTML signature.",
    bg: "linen",
  },
  {
    file: "email-signature.html",
    label: "Email Signature · HTML",
    caption: "Ready-to-paste HTML template for Gmail, Outlook, Apple Mail. Replace [Name] and contact placeholders.",
    bg: "obsidian",
    isHtml: true,
  },
];

// Mosaic tiles, indexed by posting order (grid fills newest-first).
const TILE_FILES = {
  pinned3: [
    "pinned3-post-01-of-3_row1-col3.png",
    "pinned3-post-02-of-3_row1-col2.png",
    "pinned3-post-03-of-3_row1-col1.png",
  ],
  grid9: [
    "grid9-post-01-of-9_row3-col3.png",
    "grid9-post-02-of-9_row3-col2.png",
    "grid9-post-03-of-9_row3-col1.png",
    "grid9-post-04-of-9_row2-col3.png",
    "grid9-post-05-of-9_row2-col2.png",
    "grid9-post-06-of-9_row2-col1.png",
    "grid9-post-07-of-9_row1-col3.png",
    "grid9-post-08-of-9_row1-col2.png",
    "grid9-post-09-of-9_row1-col1.png",
  ],
  grid6: [
    "grid6-post-01-of-6_row2-col3.png",
    "grid6-post-02-of-6_row2-col2.png",
    "grid6-post-03-of-6_row2-col1.png",
    "grid6-post-04-of-6_row1-col3.png",
    "grid6-post-05-of-6_row1-col2.png",
    "grid6-post-06-of-6_row1-col1.png",
  ],
};

const IG_MOSAIC = [
  {
    slug: "pinned3",
    label: "Pinned Banner · 3 Tiles",
    tiles: 3,
    caption: "The three pinnable slots as one continuous banner. TIMBER over The and TILE beneath it; CARPET over Awaits and STONE beneath it. Every word is set at one size on one baseline and kept inside the middle 810×1080 of each tile — measured from a live profile, that is all Instagram's grid actually shows — so nothing crowds a seam. The bronze hairlines run full width to stitch the row together. Pin all three and it stays locked at the top permanently.",
    wide: true,
  },
  {
    slug: "grid9",
    label: "Grid Mosaic · 9 Tiles",
    tiles: 9,
    caption: "Recreates the hero across three rows — headline, the four material pieces around the assembled mark, then the tagline. Post in the numbered order and the grid assembles itself.",
  },
  {
    slug: "grid6",
    label: "Grid Mosaic · 6 Tiles",
    tiles: 6,
    caption: "Two-row version for a lighter launch. Same headline row, timber and travertine flanking the mark.",
  },
];

const IG_PROFILES = [
  {
    file: "profile-a-full-bleed.png",
    label: "Profile · Full Bleed",
    caption: "1080×1080. The puzzle mark edge to edge — the interlocking joint sits dead centre, so it's what survives the circular crop. Recommended.",
  },
  {
    file: "profile-b-framed.png",
    label: "Profile · Framed",
    caption: "1080×1080. Mark inset on obsidian with a bronze hairline ring. Calmest read at small sizes.",
  },
  {
    file: "profile-c-monogram.png",
    label: "Profile · Monogram",
    caption: "1080×1080. MFS serif monogram over DRUMMOYNE. Use when the mark appears elsewhere in the post.",
  },
  {
    file: "profile-d-framed-mono.png",
    label: "Profile · Mono Mark",
    caption: "1080×1080. Single-colour logo variant on obsidian.",
  },
];

const MONTH_POSTS = [
  "01-slip-ratings",
  "02-board-widths",
  "03-wear-layer",
  "04-grout",
  "05-herringbone-chevron",
  "06-stone-inside-out",
  "07-wool-or-hybrid",
  "08-room-by-room",
  "09-underfloor-heating",
  "10-five-questions",
  "11-the-hour",
  "12-care",
];

const ELEMENT_REELS = [
  { slug: "timber", n: 2, label: "Timber", guide: "The Timber Guide" },
  { slug: "carpet", n: 3, label: "Carpet", guide: "The Carpet Guide" },
  { slug: "tile", n: 4, label: "Tile", guide: "The Tile Guide" },
  { slug: "stone", n: 5, label: "Stone", guide: "The Stone Guide" },
];

const REELS = [
  {
    kind: "post",
    file: "quadrant-reel-post-1080x1350.mp4",
    label: "Feed post · 1080×1350",
    meta: "4:5 · 16.6s · 30fps · H.264 · no audio",
  },
  {
    kind: "story",
    file: "quadrant-reel-story-1080x1920.mp4",
    label: "Story / Reel · 1080×1920",
    meta: "9:16 · 16.6s · 30fps · type inside the Story safe area",
  },
];

const IG_QUADRANT = [
  "quadrant-01-cover.png",
  "quadrant-02-timber.png",
  "quadrant-03-carpet.png",
  "quadrant-04-tile.png",
  "quadrant-05-stone.png",
  "quadrant-06-visit.png",
];

const IG_PIECE_HIGHLIGHTS = [
  { file: "highlight-piece-timber.png", label: "Timber" },
  { file: "highlight-piece-carpet.png", label: "Carpet" },
  { file: "highlight-piece-tile.png", label: "Tile" },
  { file: "highlight-piece-stone.png", label: "Stone" },
];

const IG_HIGHLIGHTS = [
  "highlight-1-the-studio.png",
  "highlight-2-timber.png",
  "highlight-3-carpet.png",
  "highlight-4-tile.png",
  "highlight-5-stone.png",
  "highlight-6-visualise.png",
  "highlight-7-projects.png",
  "highlight-8-samples.png",
  "highlight-9-visit.png",
];

const IG_COPY = [
  { label: "Handle", value: "@myfloorstudio_au" },
  { label: "Name field · 30 char", value: "My Floor Studio · Flooring" },
  {
    label: "Bio · Curator",
    value: "Timber · Carpet · Tile · Stone — under one roof.\nSydney's selection studio for considered floors.\nDrummoyne · By appointment",
  },
  {
    label: "Bio · Trade",
    value: "A Drummoyne selection studio.\nSpecification advice for architects, designers & considered homes.\nTimber · Carpet · Tile · Stone",
  },
];

// Print-ready vector logo variants — every file is the actual brand mark
// (realistic material puzzle) embedded as a print-quality raster inside a
// vector container. Each variant ships in four formats: SVG · PDF · EPS · AI.
const PRINT_VECTORS = [
  {
    slug: "logo-primary",
    label: "Logo · Bronze on Obsidian",
    caption: "The signature brand mark on the obsidian frame — matches every dark surface, header and signage.",
    bg: "obsidian",
    preview: "/logo/print-kit/primary-4k.png",
  },
  {
    slug: "logo-primary-cream",
    label: "Logo · on Cream Backdrop",
    caption: "Composited onto the cream stock colour — use for letterheads, business cards on natural paper.",
    bg: "linen",
    preview: "/logo/print-kit/letterhead-cream.png",
  },
  {
    slug: "logo-primary-transparent",
    label: "Logo · Transparent",
    caption: "Alpha channel preserved — place the puzzle mark over any photograph, texture or brand asset.",
    bg: "obsidian",
    preview: "/logo/print-kit/signature-icon.png",
  },
];

function Header({ onLogout }) {
  return (
    <header className="sticky top-0 z-40 bg-[#121315]/85 backdrop-blur-md border-b border-[#D6D1CA]/10">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-6 flex items-center justify-between">
        <Link to="/admin" className="flex items-center gap-4 group" data-testid="brand-back-link">
          <ArrowLeft className="w-4 h-4 text-[#C9A876] transition-transform group-hover:-translate-x-1" />
          <img src="/logo/logo-primary.png" alt="My Floor Studio" className="w-10 h-10" />
          <div>
            <p className="text-[#C9A876] text-[9px] tracking-[0.4em] uppercase">Studio</p>
            <p className="text-[#F5F4F0] text-sm tracking-[0.05em] font-serif-display italic">
              Brand kit
            </p>
          </div>
        </Link>
        <button
          onClick={onLogout}
          data-testid="brand-logout-btn"
          className="flex items-center gap-2 text-[#D6D1CA]/60 hover:text-[#F5F4F0] transition text-[11px] tracking-[0.3em] uppercase"
        >
          <LogOut className="w-3.5 h-3.5" />
          Sign out
        </button>
      </div>
    </header>
  );
}

function KitCard({ item }) {
  const src = `/logo/print-kit/${item.file}`;
  const bg = item.bg === "linen" ? "bg-[#E8E0D0]" : "bg-[#121315]";
  return (
    <motion.article
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.5, ease: "easeOut" }}
      className="border border-[#D6D1CA]/12 rounded-md overflow-hidden bg-[#151618]"
      data-testid={`brand-card-${item.file}`}
    >
      <div className={`${bg} aspect-[16/9] flex items-center justify-center p-8`}>
        {item.isHtml ? (
          <div className="flex flex-col items-center gap-4 text-[#C9A876]">
            <Code2 className="w-16 h-16" strokeWidth={1.2} />
            <span className="text-[10px] tracking-[0.3em] uppercase">HTML template</span>
          </div>
        ) : (
          <img
            src={src}
            alt={item.label}
            className="max-w-full max-h-full object-contain"
            loading="lazy"
          />
        )}
      </div>
      <div className="p-6 space-y-3">
        <div>
          <h3 className="font-serif-display italic text-[#F5F4F0] text-2xl leading-tight">
            {item.label}
          </h3>
          <p className="text-[#D6D1CA]/55 text-sm mt-2 font-body">{item.caption}</p>
        </div>
        <div className="flex items-center gap-3 pt-2">
          <a
            href={src}
            download={item.file}
            data-testid={`brand-download-${item.file}`}
            className="btn-bronze rounded-full px-5 py-2 text-[10px] tracking-[0.22em] uppercase font-medium inline-flex items-center gap-2"
          >
            <Download className="w-3.5 h-3.5" />
            Download
          </a>
          <a
            href={src}
            target="_blank"
            rel="noreferrer"
            className="text-[#C9A876] hover:text-[#F5F4F0] text-[10px] tracking-[0.22em] uppercase transition"
          >
            {item.isHtml ? "View code" : "Open in new tab"}
          </a>
        </div>
      </div>
    </motion.article>
  );
}

function VectorCard({ item }) {
  const bg = item.bg === "linen" ? "bg-[#E8E0D0]" : "bg-[#121315]";
  const formats = ["svg", "pdf", "eps", "ai"];
  return (
    <motion.article
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.5, ease: "easeOut" }}
      className="border border-[#D6D1CA]/12 rounded-md overflow-hidden bg-[#151618]"
      data-testid={`brand-vector-${item.slug}`}
    >
      <div className={`${bg} aspect-[16/9] flex items-center justify-center p-8`}>
        <img
          src={item.preview}
          alt={item.label}
          loading="lazy"
          className="max-h-full max-w-[55%] object-contain"
        />
      </div>
      <div className="p-6 space-y-3">
        <div>
          <h3 className="font-serif-display italic text-[#F5F4F0] text-2xl leading-tight">
            {item.label}
          </h3>
          <p className="text-[#D6D1CA]/55 text-sm mt-2 font-body">{item.caption}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2 pt-2">
          {formats.map((fmt) => (
            <a
              key={fmt}
              href={`/logo/print-kit/vector/${item.slug}.${fmt}`}
              download={`${item.slug}.${fmt}`}
              data-testid={`brand-vector-download-${item.slug}-${fmt}`}
              className="btn-bronze rounded-full px-4 py-2 text-[10px] tracking-[0.22em] uppercase font-medium inline-flex items-center gap-1.5"
            >
              <Download className="w-3 h-3" />
              .{fmt}
            </a>
          ))}
        </div>
      </div>
    </motion.article>
  );
}

function InstagramSection() {
  const base = "/logo/print-kit/instagram";
  return (
    <section className="mt-24" data-testid="brand-instagram-section">
      <div className="max-w-3xl mb-10">
        <p className="text-[#C9A876] text-[10px] tracking-[0.4em] uppercase mb-4">Instagram</p>
        <h2 className="font-serif-display italic text-[#F5F4F0] font-light tracking-tight text-3xl md:text-5xl leading-[1.02]">
          Profile, covers and copy.
        </h2>
        <p className="text-[#D6D1CA]/60 mt-4 text-base font-body max-w-xl">
          Everything for <span className="text-[#C9A876]">@myfloorstudio_au</span> — profile
          picture options, the nine story highlight covers, and the bio copy. Covers keep all
          artwork inside the centre safe zone so nothing clips in the circular crop.
        </p>
        <div className="mt-8 flex flex-wrap items-center gap-6">
          <a
            href={`${base}/my-floor-studio-instagram-kit.zip`}
            download="my-floor-studio-instagram-kit.zip"
            data-testid="brand-instagram-download-zip"
            className="btn-bronze rounded-full px-8 py-4 text-[11px] tracking-[0.28em] uppercase font-medium inline-flex items-center gap-3"
          >
            <Package className="w-4 h-4" />
            Download Instagram Kit (.zip)
          </a>
          <a
            href={`${base}/README.txt`}
            target="_blank"
            rel="noreferrer"
            data-testid="brand-instagram-readme"
            className="text-[#C9A876] hover:text-[#F5F4F0] text-[10px] tracking-[0.28em] uppercase transition"
          >
            Bio · Hashtags · Tone rules
          </a>
        </div>
      </div>

      {/* Four-quadrant carousel — matches the homepage Gallery section */}
      <div className="mb-16" data-testid="brand-instagram-quadrant">
        <p className="text-[#C9A876] text-[10px] tracking-[0.35em] uppercase mb-4">
          Carousel · Four-quadrant elements
        </p>
        <p className="text-[#D6D1CA]/60 text-sm font-body max-w-2xl">
          Six 1080×1350 slides — cover, one per element, then the appointment slide. The quadrant
          block sits in exactly the same position on every slide, so Canva's{" "}
          <span className="text-[#C9A876]">Match &amp; Move</span> transition morphs it between
          pages and you can export the whole thing as a Reel. Post as a carousel in order for the
          static version. Same geometry as the homepage Gallery section.
        </p>
        <div className="mt-6 flex flex-wrap items-center gap-6">
          <a
            href={`${base}/quadrant/my-floor-studio-quadrant-carousel.zip`}
            download="my-floor-studio-quadrant-carousel.zip"
            data-testid="brand-quadrant-download-zip"
            className="btn-bronze rounded-full px-7 py-3.5 text-[11px] tracking-[0.28em] uppercase font-medium inline-flex items-center gap-3"
          >
            <Package className="w-4 h-4" />
            Download carousel (.zip)
          </a>
          <a
            href={`${base}/quadrant/README-quadrant.md`}
            target="_blank"
            rel="noreferrer"
            data-testid="brand-quadrant-readme"
            className="text-[#C9A876] hover:text-[#F5F4F0] text-[10px] tracking-[0.28em] uppercase transition"
          >
            Match &amp; Move recipe · captions
          </a>
        </div>
        <div className="mt-8 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">          {IG_QUADRANT.map((f) => (
            <a
              key={f}
              href={`${base}/quadrant/${f}`}
              download={f}
              data-testid={`brand-quadrant-download-${f}`}
              className="group border border-[#D6D1CA]/12 rounded-md overflow-hidden bg-[#151618] block"
            >
              <img
                src={`${base}/quadrant/${f}`}
                alt={f.replace(/^quadrant-\d+-|\.png$/g, "").replace(/-/g, " ")}
                loading="lazy"
                className="w-full aspect-[4/5] object-cover group-hover:opacity-85 transition"
              />
              <p className="text-[#D6D1CA]/70 text-[9px] tracking-[0.25em] uppercase px-3 py-3">
                {f.replace(/\.png$/, "").replace(/^quadrant-/, "").replace(/-/g, " ")}
              </p>
            </a>
          ))}
        </div>
      </div>

      {/* Rendered reels — no Canva needed */}
      <div className="mb-16" data-testid="brand-instagram-reel">
        <p className="text-[#C9A876] text-[10px] tracking-[0.35em] uppercase mb-4">
          Video · Ready to post
        </p>
        <p className="text-[#D6D1CA]/60 text-sm font-body max-w-2xl">
          The same quadrant morph rendered as MP4 — 16.6 seconds, 30fps, no audio (add music in
          the app). Upload straight from your phone.
        </p>
        <div className="mt-8 grid md:grid-cols-2 gap-8">
          {REELS.map((r) => (
            <article
              key={r.file}
              className="border border-[#D6D1CA]/12 rounded-md overflow-hidden bg-[#151618]"
              data-testid={`brand-reel-card-${r.kind}`}
            >
              <video
                src={`${base}/quadrant/${r.file}`}
                data-testid={`brand-reel-video-${r.kind}`}
                controls
                muted
                loop
                playsInline
                preload="metadata"
                poster={`${base}/quadrant/quadrant-01-cover.png`}
                className="w-full bg-[#121315]"
                style={{ maxHeight: 520, objectFit: "contain" }}
              />
              <div className="p-6">
                <h4 className="text-[#F5F4F0] font-serif-display text-xl font-light">{r.label}</h4>
                <p className="text-[#D6D1CA]/55 text-xs font-body mt-1">{r.meta}</p>
                <a
                  href={`${base}/quadrant/${r.file}`}
                  download={r.file}
                  data-testid={`brand-reel-download-${r.kind}`}
                  className="btn-bronze inline-flex items-center gap-3 rounded-full px-6 py-3 mt-5 text-[10px] tracking-[0.28em] uppercase font-medium"
                >
                  <Download className="w-4 h-4" />
                  Download MP4
                </a>
              </div>
            </article>
          ))}
        </div>
        <a
          href={`${base}/quadrant/README-reel.md`}
          target="_blank"
          rel="noreferrer"
          data-testid="brand-reel-spec"
          className="inline-block mt-6 text-[#C9A876] hover:text-[#F5F4F0] text-[10px] tracking-[0.28em] uppercase transition"
        >
          Version spec · posting notes · captions
        </a>
      </div>

      {/* Per-element reels, each ending on that guide */}
      <div className="mb-16" data-testid="brand-element-reels">
        <p className="text-[#C9A876] text-[10px] tracking-[0.35em] uppercase mb-4">
          Video · One reel per element
        </p>
        <p className="text-[#D6D1CA]/60 text-sm font-body max-w-2xl">
          11.5 seconds each, crossfaded end to end: the room, the quadrant block, three
          specification facts, then that element's guide download. Feed and story versions.
        </p>
        <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {ELEMENT_REELS.map((r) => (
            <article
              key={r.slug}
              className="border border-[#D6D1CA]/12 rounded-md overflow-hidden bg-[#151618]"
              data-testid={`brand-element-reel-${r.slug}`}
            >
              <video
                src={`${base}/quadrant/element-reel-${r.slug}-post-1080x1350.mp4`}
                data-testid={`brand-element-reel-video-${r.slug}`}
                controls
                muted
                loop
                playsInline
                preload="metadata"
                poster={`${base}/quadrant/quadrant-0${r.n}-${r.slug}.png`}
                className="w-full bg-[#121315]"
              />
              <div className="p-5">
                <h4 className="text-[#F5F4F0] font-serif-display text-lg font-light">{r.label}</h4>
                <p className="text-[#D6D1CA]/50 text-[11px] font-body mt-1">
                  Ends on {r.guide}
                </p>
                <div className="mt-4 flex flex-col gap-2">
                  <a
                    href={`${base}/quadrant/element-reel-${r.slug}-post-1080x1350.mp4`}
                    download
                    data-testid={`brand-element-reel-post-${r.slug}`}
                    className="text-[#C9A876] hover:text-[#F5F4F0] text-[10px] tracking-[0.25em] uppercase transition"
                  >
                    ↓ Feed 1080×1350
                  </a>
                  <a
                    href={`${base}/quadrant/element-reel-${r.slug}-story-1080x1920.mp4`}
                    download
                    data-testid={`brand-element-reel-story-${r.slug}`}
                    className="text-[#C9A876] hover:text-[#F5F4F0] text-[10px] tracking-[0.25em] uppercase transition"
                  >
                    ↓ Story 1080×1920
                  </a>
                </div>
              </div>
            </article>
          ))}
        </div>
      </div>

      {/* Four-week posting calendar */}
      <div className="mb-16 border border-[#8C7355]/25 bg-[#151618] p-7 md:p-8" data-testid="brand-posting-calendar">
        <p className="text-[#C9A876] text-[10px] tracking-[0.35em] uppercase mb-3">
          Plan · Four-week calendar
        </p>
        <p className="text-[#D6D1CA]/60 text-sm font-body max-w-2xl">
          Two feed posts and three stories a week for a month, every slot matched to an asset in
          this kit, with the caption, the hashtags for the first comment, the story sticker and the
          link to use. Ends with a repeatable loop for week five onwards.
        </p>
        <a
          href={`${base}/POSTING-CALENDAR-4-WEEKS.md`}
          target="_blank"
          rel="noreferrer"
          data-testid="brand-posting-calendar-link"
          className="btn-bronze inline-flex items-center gap-3 rounded-full px-7 py-3.5 mt-6 text-[11px] tracking-[0.28em] uppercase font-medium"
        >
          <Calendar className="w-4 h-4" />
          Open the calendar
        </a>
      </div>

      {/* Twelve scheduled posts */}
      <div className="mb-16" data-testid="brand-month-posts">
        <p className="text-[#C9A876] text-[10px] tracking-[0.35em] uppercase mb-4">
          Twelve posts · the next month
        </p>
        <p className="text-[#D6D1CA]/60 text-sm font-body max-w-2xl">
          Three a week for four weeks — teaching posts on specification, then the studio itself.
          Every caption ends with a save, a comment prompt, a DM ask or the phone number, so none of
          them need a link slot. 1080×1350, post as single images.
        </p>
        <div className="mt-6 flex flex-wrap items-center gap-6">
          <a
            href={`${base}/month/my-floor-studio-12-posts.zip`}
            download="my-floor-studio-12-posts.zip"
            data-testid="brand-month-zip"
            className="btn-bronze rounded-full px-7 py-3.5 text-[11px] tracking-[0.28em] uppercase font-medium inline-flex items-center gap-3"
          >
            <Package className="w-4 h-4" />
            Download all twelve (.zip)
          </a>
          <a
            href={`${base}/month/CAPTIONS-AND-SCHEDULE.md`}
            target="_blank"
            rel="noreferrer"
            data-testid="brand-month-captions"
            className="text-[#C9A876] hover:text-[#F5F4F0] text-[10px] tracking-[0.28em] uppercase transition"
          >
            Captions · schedule · hashtags
          </a>
        </div>
        <div className="mt-8 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
          {MONTH_POSTS.map((slug) => (
            <a
              key={slug}
              href={`${base}/month/post-${slug}.png`}
              download
              data-testid={`brand-month-post-${slug}`}
              className="group border border-[#D6D1CA]/12 rounded-md overflow-hidden bg-[#151618] block"
            >
              <img
                src={`${base}/month/post-${slug}.png`}
                alt={slug.slice(3).replace(/-/g, " ")}
                loading="lazy"
                className="w-full aspect-[4/5] object-cover group-hover:opacity-85 transition"
              />
              <p className="text-[#D6D1CA]/70 text-[9px] tracking-[0.22em] uppercase px-3 py-3">
                {slug.replace(/-/g, " ")}
              </p>
            </a>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-testid="brand-instagram-profiles">
        {IG_PROFILES.map((item) => (
          <article
            key={item.file}
            className="border border-[#D6D1CA]/12 rounded-md overflow-hidden bg-[#151618]"
            data-testid={`brand-instagram-card-${item.file}`}
          >
            <div className="bg-[#121315] p-10 flex items-center justify-center">
              <img
                src={`${base}/${item.file}`}
                alt={item.label}
                loading="lazy"
                className="w-52 h-52 rounded-full object-cover"
              />
            </div>
            <div className="p-6 space-y-3">
              <h3 className="font-serif-display italic text-[#F5F4F0] text-2xl leading-tight">{item.label}</h3>
              <p className="text-[#D6D1CA]/55 text-sm font-body">{item.caption}</p>
              <a
                href={`${base}/${item.file}`}
                download={item.file}
                data-testid={`brand-instagram-download-${item.file}`}
                className="btn-bronze rounded-full px-5 py-2 text-[10px] tracking-[0.22em] uppercase font-medium inline-flex items-center gap-2"
              >
                <Download className="w-3.5 h-3.5" />
                Download
              </a>
            </div>
          </article>
        ))}
      </div>

      <p className="text-[#C9A876] text-[10px] tracking-[0.4em] uppercase mt-16 mb-6">
        Grid Mosaic · Pinned Top Posts
      </p>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8" data-testid="brand-instagram-mosaic">
        {IG_MOSAIC.map((m) => (
          <article
            key={m.slug}
            className={`border border-[#D6D1CA]/12 rounded-md overflow-hidden bg-[#151618] ${m.wide ? "lg:col-span-2" : ""}`}
            data-testid={`brand-instagram-mosaic-${m.slug}`}
          >
            <div className="bg-[#121315] p-8 flex items-center justify-center">
              <img
                src={`${base}/mosaic/${m.slug}-GRID-PREVIEW.png`}
                alt={`${m.label} assembled grid preview`}
                loading="lazy"
                className="max-h-[520px] w-auto object-contain"
              />
            </div>
            <div className="p-6 space-y-4">
              <h3 className="font-serif-display italic text-[#F5F4F0] text-2xl leading-tight">{m.label}</h3>
              <p className="text-[#D6D1CA]/55 text-sm font-body">{m.caption}</p>
              <div className="flex flex-wrap gap-2">
                {Array.from({ length: m.tiles }, (_, i) => i + 1).map((n) => (
                  <a
                    key={n}
                    href={`${base}/mosaic/${TILE_FILES[m.slug][n - 1]}`}
                    download={TILE_FILES[m.slug][n - 1]}
                    data-testid={`brand-mosaic-download-${m.slug}-${n}`}
                    className="border border-[#C9A876]/40 text-[#C9A876] hover:bg-[#C9A876] hover:text-[#121315] rounded-full w-11 h-11 inline-flex items-center justify-center text-[11px] tracking-[0.1em] font-body"
                    style={{ transition: "background-color 0.3s ease, color 0.3s ease" }}
                    title={`Post ${n} of ${m.tiles}`}
                  >
                    {n}
                  </a>
                ))}
              </div>
              <p className="text-[#D6D1CA]/45 text-xs font-body">
                Numbers are the posting order — tap each to download. Instagram fills the grid
                newest-first, so post 1 goes up first and the last tile lands top-left.
              </p>
              <a
                href={`${base}/mosaic/${m.slug}-POSTING-ORDER.txt`}
                target="_blank"
                rel="noreferrer"
                data-testid={`brand-mosaic-order-${m.slug}`}
                className="text-[#C9A876] hover:text-[#F5F4F0] text-[10px] tracking-[0.22em] uppercase transition inline-block"
              >
                Posting + pinning instructions
              </a>
            </div>
          </article>
        ))}
      </div>

      <p className="text-[#C9A876] text-[10px] tracking-[0.4em] uppercase mt-16 mb-3">
        Material Highlight Covers · One Per Element
      </p>
      <p className="text-[#D6D1CA]/55 text-sm font-body max-w-xl mb-6">
        Four covers heroing the puzzle pieces — Timber, Carpet, Tile, Stone — for the
        highlights where you post content on each element. No lettering: Instagram crops
        covers to a circle from the centre of the frame, so any label would sit outside it
        and never be seen. The piece is the icon.
      </p>
      <div
        className="grid grid-cols-2 md:grid-cols-4 gap-4"
        data-testid="brand-instagram-piece-highlights"
      >
        {IG_PIECE_HIGHLIGHTS.map((p) => (
          <a
            key={p.file}
            href={`${base}/${p.file}`}
            download={p.file}
            data-testid={`brand-instagram-download-${p.file}`}
            className="group border border-[#D6D1CA]/12 rounded-md overflow-hidden bg-[#121315] hover:border-[#C9A876]/50 p-6 flex flex-col items-center gap-4"
            style={{ transition: "border-color 0.4s ease" }}
          >
            <img
              src={`${base}/${p.file}`}
              alt={`${p.label} puzzle piece highlight cover`}
              loading="lazy"
              className="w-32 h-32 rounded-full object-cover"
            />
            <span className="text-[#C9A876] text-[10px] tracking-[0.3em] uppercase">
              {p.label}
            </span>
          </a>
        ))}
      </div>

      <p className="text-[#C9A876] text-[10px] tracking-[0.4em] uppercase mt-16 mb-6">
        Story Highlight Covers · 1080×1920
      </p>
      <div
        className="grid grid-cols-3 md:grid-cols-5 lg:grid-cols-9 gap-3"
        data-testid="brand-instagram-highlights"
      >
        {IG_HIGHLIGHTS.map((file) => (
          <a
            key={file}
            href={`${base}/${file}`}
            download={file}
            data-testid={`brand-instagram-download-${file}`}
            className="group border border-[#D6D1CA]/12 rounded-md overflow-hidden bg-[#121315] hover:border-[#C9A876]/50"
            style={{ transition: "border-color 0.4s ease" }}
          >
            <img
              src={`${base}/${file}`}
              alt={file.replace(/^highlight-\d+-|\.png$/g, "").replace(/-/g, " ")}
              loading="lazy"
              className="w-full aspect-[9/16] object-cover opacity-85 group-hover:opacity-100"
              style={{ transition: "opacity 0.4s ease" }}
            />
          </a>
        ))}
      </div>

      <p className="text-[#C9A876] text-[10px] tracking-[0.4em] uppercase mt-16 mb-6">Profile Copy</p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="brand-instagram-copy">
        {IG_COPY.map((c) => (
          <div key={c.label} className="border border-[#D6D1CA]/12 rounded-md bg-[#151618] p-6">
            <p className="text-[#C9A876] text-[9px] tracking-[0.3em] uppercase">{c.label}</p>
            <p className="text-[#F5F4F0] text-sm font-body mt-3 whitespace-pre-line leading-relaxed">
              {c.value}
            </p>
          </div>
        ))}
      </div>
    </section>
  );
}

function BrandView({ onLogout }) {
  return (
    <div className="min-h-screen bg-[#121315] grain">
      <Header onLogout={onLogout} />
      <main className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-24">
        <div className="max-w-3xl mb-16">
          <p className="text-[#C9A876] text-[10px] tracking-[0.4em] uppercase mb-6">
            Print Kit
          </p>
          <h1 className="font-serif-display italic text-[#F5F4F0] font-light tracking-tight text-4xl md:text-6xl leading-[0.98]">
            Every asset the studio needs, one click away.
          </h1>
          <p className="text-[#D6D1CA]/60 mt-6 text-base font-body max-w-xl">
            High-resolution logo lockups, business cards and print-ready assets — every
            file in the studio's brand system, one click away.
          </p>
          <div className="mt-10 flex flex-wrap items-center gap-6">
            <a
              href="/logo/print-kit/my-floor-studio-brand-kit.zip"
              download="my-floor-studio-brand-kit.zip"
              data-testid="brand-download-zip"
              className="btn-bronze rounded-full px-8 py-4 text-[11px] tracking-[0.28em] uppercase font-medium inline-flex items-center gap-3"
            >
              <Package className="w-4 h-4" />
              Download Entire Kit (.zip)
            </a>
            <span className="text-[#D6D1CA]/45 text-[10px] tracking-[0.3em] uppercase">
              18 raster + 12 print vectors + 13 Instagram assets + README
            </span>
          </div>
        </div>

        {/* Brand Reference — Type & Colour */}
        <section
          data-testid="brand-reference"
          className="mb-20 grid grid-cols-1 lg:grid-cols-2 gap-6 border-y border-[#D6D1CA]/12 py-12"
        >
          {/* Typography */}
          <div className="lg:pr-10 lg:border-r lg:border-[#D6D1CA]/12">
            <p className="text-[#C9A876] text-[10px] tracking-[0.4em] uppercase mb-6">Typography</p>
            <div className="space-y-8">
              <div>
                <div className="font-serif-display text-[#F5F4F0] text-5xl md:text-6xl font-light leading-none italic">
                  Cormorant Garamond
                </div>
                <div className="mt-3 flex items-baseline gap-4 text-[10px] tracking-[0.28em] uppercase text-[#D6D1CA]/60">
                  <span className="text-[#C9A876]">Display</span>
                  <span>Headlines · Editorial titles · Wordmark</span>
                </div>
                <p className="mt-2 text-[#D6D1CA]/45 text-xs font-body">
                  Weights: 300 · 400 · 500 (roman + italic)
                </p>
              </div>
              <div>
                <div className="font-body text-[#F5F4F0] text-3xl md:text-4xl font-medium leading-tight">
                  Plus Jakarta Sans
                </div>
                <div className="mt-3 flex items-baseline gap-4 text-[10px] tracking-[0.28em] uppercase text-[#D6D1CA]/60">
                  <span className="text-[#C9A876]">Body</span>
                  <span>UI · Nav · Body copy · Captions</span>
                </div>
                <p className="mt-2 text-[#D6D1CA]/45 text-xs font-body">
                  Weights: 300 · 400 · 500 · 600 · 700
                </p>
              </div>
            </div>
          </div>

          {/* Colour palette */}
          <div className="lg:pl-10">
            <p className="text-[#C9A876] text-[10px] tracking-[0.4em] uppercase mb-6">Colour Palette</p>
            <div className="grid grid-cols-2 gap-3">
              {[
                { name: "Obsidian", hex: "#121315", role: "Primary surface", swatch: "#121315", ring: "border border-[#D6D1CA]/20" },
                { name: "Warm Greige", hex: "#D6D1CA", role: "Neutral text · Rules", swatch: "#D6D1CA" },
                { name: "Off-White Linen", hex: "#F5F4F0", role: "Primary text on dark", swatch: "#F5F4F0" },
                { name: "Burnished Bronze", hex: "#8C7355", role: "Muted accent · Hairlines", swatch: "#8C7355" },
                { name: "Bronze Highlight", hex: "#C9A876", role: "Kickers · Interactive", swatch: "#C9A876" },
                { name: "Print Bronze", hex: "#A2794F", role: "Print accent · Logo outline", swatch: "#A2794F" },
              ].map((c) => (
                <div key={c.hex} className="flex items-center gap-3" data-testid={`brand-swatch-${c.hex.replace('#','').toLowerCase()}`}>
                  <span
                    className={`w-10 h-10 rounded-sm flex-shrink-0 ${c.ring || ""}`}
                    style={{ backgroundColor: c.swatch }}
                  />
                  <div className="min-w-0">
                    <div className="font-serif-display text-[#F5F4F0] italic text-sm md:text-base leading-tight truncate">{c.name}</div>
                    <div className="text-[#C9A876] text-[9px] tracking-[0.2em] uppercase font-body">{c.hex}</div>
                    <div className="text-[#D6D1CA]/50 text-[10px] font-body truncate">{c.role}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-testid="brand-kit-grid">
          {KIT.map((item) => (
            <KitCard key={item.file} item={item} />
          ))}
        </div>

        {/* Print-ready vector logos — SVG · PDF · EPS · AI */}
        <section className="mt-24" data-testid="brand-vectors-section">
          <div className="max-w-3xl mb-10">
            <p className="text-[#C9A876] text-[10px] tracking-[0.4em] uppercase mb-4">
              Print Vectors
            </p>
            <h2 className="font-serif-display italic text-[#F5F4F0] font-light tracking-tight text-3xl md:text-5xl leading-[1.02]">
              Ready-to-print logo files.
            </h2>
            <p className="text-[#D6D1CA]/60 mt-4 text-base font-body max-w-xl">
              Every logo variant as a proper editable vector — hand to any
              printer, sign-writer or designer. SVG for the web, PDF for
              InDesign placement, EPS for legacy RIPs, AI to open natively in
              Adobe Illustrator.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-testid="brand-vectors-grid">
            {PRINT_VECTORS.map((item) => (
              <VectorCard key={item.slug} item={item} />
            ))}
          </div>
        </section>

        <InstagramSection />
      </main>
    </div>
  );
}

export default function Brand() {
  useSEO({
    title: "Brand Kit — My Floor Studio",
    description: "Private brand asset library.",
    path: "/brand",
    noindex: true,
  });
  const [key, setKey] = useState(() => sessionStorage.getItem(KEY_STORAGE) || "");

  useEffect(() => {
    if (!key) return;
    adminPing(key).catch(() => {
      sessionStorage.removeItem(KEY_STORAGE);
      setKey("");
    });
  }, [key]);

  const logout = () => {
    sessionStorage.removeItem(KEY_STORAGE);
    setKey("");
  };

  return key ? <BrandView onLogout={logout} /> : <Gate onAuthed={setKey} />;
}
